import { useCallback, useMemo } from "react"
import { ComponentMetadata, ErrorLogInput, ErrorRecord } from "../types/errors"
import { useErrorHandlingContext } from "../providers/ErrorProvider"

interface HandleErrorOptions
  extends Omit<ErrorLogInput, "error" | "component" | "metadata" | "severity"> {
  severity?: ErrorLogInput["severity"]
}

export const useErrorHandler = (
  componentName: string,
  metadata?: ComponentMetadata,
): {
  errors: ErrorRecord[]
  handleError: (error: unknown, options?: HandleErrorOptions) => ErrorRecord
  clearComponentErrors: () => void
  wrapAsync: <T>(fn: () => Promise<T>, options?: HandleErrorOptions) => Promise<T | null>
} => {
  const { logError, getErrorsByComponent, clearByComponent } = useErrorHandlingContext()

  const handleError = useCallback(
    (error: unknown, options?: HandleErrorOptions) =>
      logError({
        error,
        component: componentName,
        metadata,
        severity: options?.severity,
        userMessage: options?.userMessage,
        operation: options?.operation,
        context: options?.context,
      }),
    [componentName, logError, metadata],
  )

  const errors = useMemo(
    () => getErrorsByComponent(componentName),
    [componentName, getErrorsByComponent],
  )

  const clearComponentErrors = useCallback(
    () => clearByComponent(componentName),
    [clearByComponent, componentName],
  )

  const wrapAsync = useCallback(
    async <T,>(fn: () => Promise<T>, options?: HandleErrorOptions): Promise<T | null> => {
      try {
        return await fn()
      } catch (error) {
        handleError(error, options)
        return null
      }
    },
    [handleError],
  )

  return { errors, handleError, clearComponentErrors, wrapAsync }
}
